/**
 * Los catálogos del sistema, servidos por el backend.
 *
 * `ARQ-8`: **no se redeclara ni una etiqueta aquí.** Las descripciones de los
 * 23 pasos, los tonos de los estados y los nombres de los cuatro canales
 * llegan de `GET /api/catalogos`, derivados de los arrays `as const` del
 * dominio. Escribir aquí `'El notificador entregó el oficio'` sería reproducir
 * exactamente el defecto de Recuperos que `ARQUITECTURA.md` §5 documenta.
 *
 * Se cargan una vez al arrancar y se cachean: son constantes del despliegue.
 */
import { createContext, useContext } from 'react'
import type {
  ActorCircuito,
  Canal,
  CausaDerivacion,
  DesenlaceConsulta,
  DesenlaceRequerimiento,
  EstadoBloque,
  EstadoConsulta,
  EstadoDocumento,
  Pronunciamiento,
  SeccionVerificable,
  TipoEntidad,
  Tono,
} from './tipos.js'

export interface Opcion<T extends string = string> {
  codigo: T
  etiqueta: string
}

export interface OpcionConTono<T extends string> extends Opcion<T> {
  tono: Tono
}

export interface PasoDeCanal {
  paso: number
  actor: ActorCircuito
  actorEtiqueta: string
  descripcion: string
  obligatorio: boolean
  evidencia: string | null
  /**
   * El permiso que hace falta para registrarlo, decidido por el servidor.
   *
   * La interfaz lo comprueba tal cual en lugar de deducirlo del actor: cuando
   * lo deducía —y mal, con un permiso fijo— le ofrecía a un coordinador los
   * pasos de Mesa de Partes, que no puede ejecutar.
   */
  permiso: string
}

export interface DefinicionCanal {
  codigo: Canal
  etiqueta: string
  pasoEntrega: number
  pasoCierre: number
  plazoPorDefectoDias: number
  exigeNumeroCargo: boolean
  /** En qué paso se pide el nº de cargo. `null` si el canal no lo pide. */
  pasoNumeroCargo: number | null
  exigeNotificador: boolean
  generaDocumento: boolean
  totalPasos: number
  pasos: readonly PasoDeCanal[]
}

export interface Catalogos {
  canales: readonly DefinicionCanal[]
  actores: ReadonlyArray<Opcion<ActorCircuito>>
  secciones: ReadonlyArray<Opcion<SeccionVerificable> & { ordinal: number }>
  tiposEntidad: ReadonlyArray<Opcion<TipoEntidad>>
  clasesEvidencia: readonly Opcion[]
  motivosRechazo: readonly Opcion[]
  causasDerivacion: ReadonlyArray<Opcion<CausaDerivacion>>
  pronunciamientos: ReadonlyArray<Opcion<Pronunciamiento>>
  desenlacesRequerimiento: ReadonlyArray<Opcion<DesenlaceRequerimiento> & { esConcluyente: boolean }>
  desenlacesConsulta: ReadonlyArray<Opcion<DesenlaceConsulta>>
  estadosDocumento: ReadonlyArray<OpcionConTono<EstadoDocumento>>
  estadosConsulta: ReadonlyArray<OpcionConTono<EstadoConsulta>>
  estadosBloque: ReadonlyArray<Opcion<EstadoBloque>>
  /** Los siete grupos de permiso, en el orden del proceso. */
  gruposDePermiso: readonly Opcion[]
}

const ContextoCatalogos = createContext<Catalogos | undefined>(undefined)

export const ProveedorCatalogos = ContextoCatalogos.Provider

export function useCatalogos(): Catalogos {
  const catalogos = useContext(ContextoCatalogos)
  if (!catalogos) {
    throw new Error('useCatalogos se usó fuera del proveedor: la app no terminó de arrancar.')
  }
  return catalogos
}

// ────────────────────────────────────────────────────────────────────────────
// Búsquedas sobre el catálogo
//
// Devuelven el código crudo si no encuentran la etiqueta, en lugar de un texto
// inventado: un código visible en pantalla es un error evidente y localizable,
// un «—» silencioso no.
// ────────────────────────────────────────────────────────────────────────────

export function etiquetaDe<T extends string>(
  opciones: ReadonlyArray<Opcion<T>>,
  codigo: T | undefined,
): string {
  if (codigo === undefined) return ''
  return opciones.find((opcion) => opcion.codigo === codigo)?.etiqueta ?? codigo
}

export function canalDe(catalogos: Catalogos, canal: Canal): DefinicionCanal | undefined {
  return catalogos.canales.find((definicion) => definicion.codigo === canal)
}

export function pasoDe(
  catalogos: Catalogos,
  canal: Canal,
  paso: number,
): PasoDeCanal | undefined {
  return canalDe(catalogos, canal)?.pasos.find((item) => item.paso === paso)
}

/** El tono con que pintar un estado. Lo decide el backend (`ARQ-4`). */
export function tonoDeEstadoDocumento(catalogos: Catalogos, estado: EstadoDocumento): Tono {
  return catalogos.estadosDocumento.find((item) => item.codigo === estado)?.tono ?? 'neutral'
}

export function tonoDeEstadoConsulta(catalogos: Catalogos, estado: EstadoConsulta): Tono {
  return catalogos.estadosConsulta.find((item) => item.codigo === estado)?.tono ?? 'neutral'
}
